import { useState } from "react";
import {
  Box,
  Collapse,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import moment from "moment-timezone";
import {
  type IPairing,
  type IViolationResult,
  ScenarioPositionStatus,
} from "@vmosolutions/oc-interfaces";
import type {
  IScenarioPositionResult,
  IScenarioViolationEvaluation,
} from "./FetchScenarioViolations";

type IScenarioViolationsPanelProps = {
  /** The pre-check result, or null before the first run. */
  result: IScenarioViolationEvaluation | null;
  loading: boolean;
  /** The live editor pairing, used to resolve friendly crew names from position crewIds. */
  storedPairing?: IPairing;
  /** Bumped each run so rows remount and re-apply their default-expanded state on a fresh pre-check. */
  runId: number;
};

type ViolationTone = "new" | "fixed" | "existing";

/** Same identity the backend diffs on - regulation + exact period + cause - so we can match current vs fixed. */
const violationKey = (v: IViolationResult): string =>
  `${v.regulation}|${v.period.start}|${v.period.end}|${v.cause ?? ""}`;

/** Friendly crew label from the pairing positions, falling back to the position code. */
const crewLabel = (
  crew: IScenarioPositionResult["crew"],
  storedPairing?: IPairing,
): string => {
  // No crewId = the un-crewed self-check row; its `position` ("Uncrewed") already carries the wording.
  if (crew.crewId == null) {
    return crew.position;
  }
  const match = storedPairing?.positions.find(
    (pos) => pos.crew?._id === crew.crewId,
  );
  const user = match?.crew;
  const name = user ? `${user.firstName} ${user.lastName}`.trim() : "";
  return name ? `${crew.position} - ${name}` : crew.position;
};

const ViolationCard = ({
  violation,
  tone,
}: {
  violation: IViolationResult;
  tone: ViolationTone;
}): JSX.Element => (
  <Paper
    elevation={tone === "existing" ? 0 : 2}
    sx={(theme) => ({
      my: 0.5,
      p: 0.75,
      borderRadius: "4px",
      fontSize: "0.75em",
      width: "100%",
      // Force white text (not the palette's contrastText, which resolves to black for success in this theme) on
      // the saturated red/green backgrounds, with a black drop shadow for legibility.
      ...(tone === "new"
        ? {
            border: `1px solid ${theme.palette.error.dark}`,
            backgroundColor: theme.palette.error.dark,
            color: theme.palette.common.white,
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.85)",
          }
        : tone === "fixed"
          ? {
              border: `1px solid ${theme.palette.success.main}`,
              backgroundColor: theme.palette.success.dark,
              color: theme.palette.common.white,
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.85)",
            }
          : {
              border: `1px solid ${theme.palette.error.main}`,
              backgroundColor: "transparent",
              color: theme.palette.text.primary,
            }),
    })}
  >
    {/* reg title / reg text on the left, start / end dates aligned on the right of their respective rows. */}
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            fontWeight: 700,
            textDecoration: tone === "fixed" ? "line-through" : undefined,
          }}
        >
          {violation.title}
        </Box>
        <Box>
          {violation.regulation}
          {violation.cause != null ? ` - ${violation.cause}` : ""}
        </Box>
      </Box>
      <Box
        sx={{
          textAlign: "right",
          whiteSpace: "nowrap",
          flexShrink: 0,
          opacity: 0.85,
        }}
      >
        <Box>{moment(violation.period.start).format("MMM-DD-YYYY HH:mm")}</Box>
        <Box>{moment(violation.period.end).format("MMM-DD-YYYY HH:mm")}</Box>
      </Box>
    </Box>
  </Paper>
);

const SectionLabel = ({ children }: { children: string }): JSX.Element => (
  <Typography
    sx={{
      fontSize: 10,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      mt: 0.5,
      opacity: 0.7,
    }}
  >
    {children}
  </Typography>
);

/** One `{count} {label}` segment of the summary line, greyed at 0 and tinted by kind (current=amber, new=red, fixed=green) when > 0. */
const CountSeg = ({
  count,
  label,
  kind,
}: {
  count: number;
  label: string;
  kind: "current" | "new" | "fixed";
}): JSX.Element => (
  <Box
    component="span"
    sx={(theme) => ({
      fontSize: 11,
      color:
        count === 0
          ? theme.palette.text.secondary
          : kind === "current"
            ? theme.palette.warning.main
            : kind === "new"
              ? theme.palette.error.main
              : theme.palette.success.main,
    })}
  >
    {count} {label}
  </Box>
);

const PositionResult = ({
  position,
  storedPairing,
}: {
  position: IScenarioPositionResult;
  storedPairing?: IPairing;
}): JSX.Element => {
  const isError =
    position.status === ScenarioPositionStatus.Conflict ||
    position.status === ScenarioPositionStatus.Error;
  const isUncrewed = position.status === ScenarioPositionStatus.Uncrewed;
  const added = position.newViolations;
  const fixed = position.resolvedViolations;
  const current = position.currentViolations;
  // Existing violations that remain after the change = current minus the ones it fixes.
  const fixedKeys = new Set(fixed.map(violationKey));
  const remaining = current.filter((v) => !fixedKeys.has(violationKey(v)));

  // Default open when the change introduces something (or errored); otherwise collapsed to keep the list compact.
  const [expanded, setExpanded] = useState<boolean>(
    added.length > 0 || isError,
  );

  return (
    <Box sx={{ mb: 1 }}>
      {/* Always-visible header: crew name + summary counts, click to expand/collapse the details below. */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          borderRadius: "4px",
          py: 0.25,
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        {expanded ? (
          <ExpandMoreIcon sx={{ fontSize: 16, opacity: 0.7 }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 16, opacity: 0.7 }} />
        )}
        <Typography sx={{ fontWeight: 600, fontSize: 12 }}>
          {crewLabel(position.crew, storedPairing)}
        </Typography>
        {!isError && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "baseline",
              flexWrap: "wrap",
              ml: 0.5,
            }}
          >
            {!isUncrewed && (
              <CountSeg count={current.length} label="current" kind="current" />
            )}
            <CountSeg count={added.length} label="new" kind="new" />
            {!isUncrewed && (
              <CountSeg count={fixed.length} label="fixed" kind="fixed" />
            )}
          </Box>
        )}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ pl: 2.5 }}>
          {isError ? (
            <Typography
              sx={(theme) => ({
                fontSize: 11,
                color: theme.palette.warning.main,
              })}
            >
              {position.error ?? "Could not be evaluated."}
            </Typography>
          ) : added.length === 0 &&
            fixed.length === 0 &&
            remaining.length === 0 ? (
            <Typography
              sx={(theme) => ({
                fontSize: 11,
                color: theme.palette.success.main,
              })}
            >
              No violations.
            </Typography>
          ) : (
            <>
              {added.length > 0 && (
                <>
                  <SectionLabel>
                    {isUncrewed ? "Violations" : "New - caused by this change"}
                  </SectionLabel>
                  {added.map((v, j) => (
                    <ViolationCard
                      key={`new-${violationKey(v)}-${j}`}
                      violation={v}
                      tone="new"
                    />
                  ))}
                </>
              )}
              {fixed.length > 0 && (
                <>
                  <SectionLabel>Fixed by this change</SectionLabel>
                  {fixed.map((v, j) => (
                    <ViolationCard
                      key={`fixed-${violationKey(v)}-${j}`}
                      violation={v}
                      tone="fixed"
                    />
                  ))}
                </>
              )}
              {remaining.length > 0 && (
                <>
                  <SectionLabel>Existing - already present</SectionLabel>
                  {remaining.map((v, j) => (
                    <ViolationCard
                      key={`existing-${violationKey(v)}-${j}`}
                      violation={v}
                      tone="existing"
                    />
                  ))}
                </>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * Inline panel for the regulations pre-check. Per assigned crew (and the un-crewed self-check) it shows the
 * crew's CURRENT violation count, the NEW violations this assignment would cause, the violations it would FIX,
 * and the existing violations that remain - so "no new violations" isn't mistaken for "no violations".
 */
export const ScenarioViolationsPanel = (
  props: IScenarioViolationsPanelProps,
): JSX.Element | null => {
  const { result, loading, storedPairing, runId } = props;
  // Whole-panel collapse (over all crew), default expanded. Declared before the early returns to satisfy the rules of hooks.
  const [expanded, setExpanded] = useState<boolean>(true);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}>
        <CircularProgress size={16} thickness={5} />
        <Typography sx={{ fontSize: 12 }}>
          Running regulations pre-check...
        </Typography>
      </Box>
    );
  }
  if (!result) {
    return null;
  }

  return (
    <Box sx={{ p: 1, overflowY: "auto" }}>
      {/* Clickable header collapses/expands the entire per-crew list below. */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          borderRadius: "4px",
          mb: 0.5,
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        {expanded ? (
          <ExpandMoreIcon sx={{ fontSize: 18, opacity: 0.7 }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 18, opacity: 0.7 }} />
        )}
        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
          Regs pre-check - violations in the schedule window
        </Typography>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        {result.positions.map((position, i) => (
          // runId in the key remounts rows on each fresh pre-check so `expanded` re-seeds from the new result.
          <PositionResult
            key={`${runId}-${position.crew.crewId ?? position.crew.position}-${i}`}
            position={position}
            storedPairing={storedPairing}
          />
        ))}
      </Collapse>
    </Box>
  );
};
